import { Box, Flex, Text } from '@workwhile/ui'
import { ChevronRight, ChevronUp, DotIcon } from 'lucide-react'
import styled from 'styled-components'

type BlockedAlertProps = {
  isOpen: boolean
  onClick: () => void
  positionTemplateBlockCount: number
  isCompanyBlocked: boolean
  workerName: string
}

const Alert = styled(Flex)<{ isOpen: boolean }>`
  align-items: center;
  background: ${({ theme }) => theme.colors.error50};
  border-color: ${({ theme }) => theme.colors.lightText};
  border-radius: ${({ theme }) => theme.radii.standard};
  border-width: 0;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.space[4]};
  padding: ${({ theme }) => theme.space[3]};
  user-select: none;
  border-left: ${({ theme }) => theme.space[1]} solid
    ${({ theme }) => theme.colors.error100};
`

const AlertHeader = styled(Flex)`
  color: ${({ theme }) => theme.colors.text};
  gap: ${({ theme }) => theme.space[2]};
  justify-content: space-between;
  align-items: top;
  width: 100%;
`

const AlertContent = styled(Flex)`
  gap: ${({ theme }) => theme.space[2]};
  padding-top: ${({ theme }) => theme.space[1]};
  padding-bottom: ${({ theme }) => theme.space[1]};
`

const AlertTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeights[2]};
  white-space: nowrap;
`

const AlertSubtitle = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes[1]};
  color: ${({ theme }) => theme.colors.lightText};
  white-space: nowrap;
`

const AlertItem = styled(Text)`
  color: ${({ theme }) => theme.colors.lightText};
  display: flex;
  gap: ${({ theme }) => theme.space[1]};
`

export const BlockedAlert = ({
  isOpen,
  onClick,
  isCompanyBlocked,
  positionTemplateBlockCount,
  workerName = 'This worker',
}: BlockedAlertProps) => {
  const copy =
    isCompanyBlocked || positionTemplateBlockCount === 0
      ? {
          title: 'Worker Blocked',
          subtitle: `${workerName} has been blocked from your company.`,
          description: [
            'This worker has been removed from all scheduled shifts.',
            'They will not be able to accept any shifts going forward.',
          ],
        }
      : {
          title: 'Worker Blocked',
          subtitle: `${workerName} has been blocked from ${positionTemplateBlockCount} position${
            positionTemplateBlockCount === 1 ? '' : 's'
          }.`,
          description: [
            'This worker has been removed from all scheduled shifts for the selected job types.',
            'They will not be able to accept new shifts for those job types when posted.',
          ],
        }

  if (!isCompanyBlocked && positionTemplateBlockCount === 0) {
    return null
  }

  return (
    <Alert isOpen={isOpen} onClick={onClick}>
      <AlertHeader>
        <AlertTitle>{copy.title}</AlertTitle>
        {isOpen ? null : <AlertSubtitle>{copy.subtitle}</AlertSubtitle>}
        <Flex color="lightText">
          {isOpen ? <ChevronUp size={20} /> : <ChevronRight size={20} />}
        </Flex>
      </AlertHeader>
      {isOpen ? (
        <AlertContent flexDirection="column">
          <AlertSubtitle>{copy.subtitle}</AlertSubtitle>
          <Box pr={4}>
            {copy.description.map((description) => (
              <AlertItem key={description} fontSize="sm">
                <Box mt={'2px'}>
                  <DotIcon strokeWidth={6} size={16} />
                </Box>
                {description}
              </AlertItem>
            ))}
          </Box>
        </AlertContent>
      ) : null}
    </Alert>
  )
}
